import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField, FormControlLabel, Checkbox, Alert, Snackbar, Box } from '@mui/material';
import { usePlanHintCreateMutation } from '../../../services/planHints';
import { useTranslation } from 'react-i18next';
import { favouritesAddItem, favouritesRemoveItem } from '../../plans/planSlice';
import { useDispatch } from 'react-redux';
import { PlanListItem } from '../../../types';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import { useAppSelector } from '../../../hooks';
import { selectUserProfile } from '../../accountSlice';
import { useAddFavouriteMutation, useRemoveFavouriteMutation } from '../../../services/plans';
import { Favorite as FavoriteIcon } from '@mui/icons-material';

export const useWishlistLogic = () => {
	const [addFavouriteMutation, { error: addFavouriteError, reset: resetAddFavouriteError }] = useAddFavouriteMutation();
	const [removeFavouriteMutation] = useRemoveFavouriteMutation();
	const userProfile = useAppSelector(selectUserProfile);
	const dispatch = useDispatch();
	const [addToWishlistPlanInfo, setAddToWishlistPlanInfo] = useState<Pick<PlanListItem, 'id' | 'title' | 'materialsCost'> | null>(null);

	const handleAddFavouriteBtnClick = (plan: Pick<PlanListItem, 'id' | 'title' | 'materialsCost'>) => {
		if (!userProfile) {
			if (!plan?.materialsCost) {
				setAddToWishlistPlanInfo(plan);
			}
		} else {
			addFavouriteMutation({ planId: plan.id });
		}
	};

	const handleRemoveFavouriteBtnClick = (planId: number) => {
		if (!userProfile) {
			dispatch(favouritesRemoveItem(planId));
			return;
		}
		removeFavouriteMutation(planId);
	};

	return {
		handleAddFavouriteBtnClick,
		handleRemoveFavouriteBtnClick,
		addToWishlistPlanInfo,
		setAddToWishlistPlanInfo,
		addFavouriteError,
		resetAddFavouriteError,
	};
};

interface WishlistDialogProps {
  open: boolean;
  plan: Pick<PlanListItem, 'id' | 'title' | 'materialsCost'> | null;
  onClose: () => void;
}

const WishlistDialog: React.FC<WishlistDialogProps> = ({ open, plan, onClose }) => {
	const { t } = useTranslation();
	const [createPlanHintMutation] = usePlanHintCreateMutation();
	const [isMarketingConsentChecked, setIsMarketingConsentChecked] = useState(false);
	const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] = useState(false);
	const dispatch = useDispatch();

	useEffect(() => {
		if (open) {
			ReactGA.event({
				category: 'UI',
				action: 'AddToWishlistDialogOpened',
				label: plan?.id.toString(),
				value: plan?.materialsCost ? Number(plan.materialsCost) : undefined,
			});
		}
	}, [open]);

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!plan) {
			console.error('plan is null');
			return;
		}
		const formData = new FormData(event.target as HTMLFormElement);
		const planHint = {
			type: 'wish',
			title: `Wishlist entry: ${plan.id}`,
			email: formData.get('email') as string,
			description: plan.id.toString(),
			marketingConsent: isMarketingConsentChecked,
			notifyMeConsent: false,
			shareInstructionsConsent: false,
		};
		createPlanHintMutation(planHint);
		setIsSuccessSnackbarOpen(true);
		dispatch(favouritesAddItem({ id: plan.id }));
		ReactPixel.track('AddToWishlist', {
			content_ids: [plan.id.toString()],
			content_name: plan.title,
			content_type: 'product',
			value: plan.materialsCost || 0, // TODO: add currency
		});
		ReactGA.event({
			category: 'E-commerce',
			action: 'Plan added to wishlist',
			label: plan.id.toString(),
		});
		onClose();
	};

	return (<>
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<form onSubmit={handleSubmit}>
				<DialogTitle>{t('addWishlistDialog.title')}</DialogTitle>
				<DialogContent>
					<Typography component="p" py={2}>
						{t('addWishlistDialog.description')}
					</Typography>
					{/* <FormControlLabel
						name="joinWishlistConsent"
						sx={{ userSelect: 'none' }}
						slotProps={{ typography: { fontSize: '0.9rem' } }}
						control={<Checkbox />}
						label={t('addWishlistDialog.joinWishlistCheckboxLabel')}
						onChange={(event, checked) => setIsJoinWishlistConsentChecked(checked)}
					/> */}
					<Box display="flex" flexDirection="column" gap={2}>
						<FormControlLabel
							name="marketingConsent"
							sx={{ userSelect: 'none' }}
							slotProps={{ typography: { fontSize: '0.9rem' } }}
							control={<Checkbox />}
							label={t('addWishlistDialog.marketingConsentCheckboxLabel')}
							onChange={(event, checked) => setIsMarketingConsentChecked(checked)}
						/>
						{isMarketingConsentChecked && <TextField
							label={t('addWishlistDialog.email')}
							type="email"
							name="email"
							fullWidth
							required
						/>}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>{t('addWishlistDialog.close')}</Button>
					<Button startIcon={<FavoriteIcon />} type="submit" variant="contained" color="primary">{t('addWishlistDialog.submit')}</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Snackbar open={isSuccessSnackbarOpen} autoHideDuration={6000} onClose={() => setIsSuccessSnackbarOpen(false)}>
			<Alert severity="success">
				{t('addWishlistDialog.success')}
			</Alert>
		</Snackbar>
	</>
	);
};

export default WishlistDialog;